import { FloatingNode } from "@floating-ui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { SUGGESTIONS_MENU_ATTR } from "@/components/suggestions/constants";
import { SuggestionGroupTitle } from "@/components/suggestions/list/SuggestionGroupTitle";
import { SuggestionsGrid } from "@/components/suggestions/list/SuggestionsGrid";
import { SuggestionsList } from "@/components/suggestions/list/SuggestionsList";
import { SuggestionsSearchInput } from "@/components/suggestions/list/SuggestionsSearchInput";
import { useMenuSearchable } from "@/components/suggestions/utils/useMenuSearchable";
import { twMerge } from "@/utils/twMerge";

import type { SuggestionsGroup } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { SuggestionsModel } from "@/models/SuggestionsModel";

type Props = Omit<React.ComponentProps<"div">, "onSelect"> & {
  doc: DocumentModel;
  suggestions: SuggestionsModel;
  listId: string | null;
  groups: SuggestionsGroup[];
  pseudoFocusIndex?: number;
};

export const SuggestionsGroupsList = observer(function SuggestionsGroupsList({
  ref,
  doc,
  suggestions,
  listId,
  className,
  groups,
  pseudoFocusIndex,
  ...rest
}: Props) {
  const t = useTranslations("suggestions");
  const searchable = useMenuSearchable(suggestions, listId);

  const isNested = !!listId;

  return (
    <FloatingNode id={listId ?? "root"}>
      <div
        ref={ref}
        role="menu"
        {...{ [SUGGESTIONS_MENU_ATTR]: "" }}
        className={twMerge(
          "relative z-50 min-w-[320px] max-h-suggestions-list max-w-[480px] overflow-auto no-scrollbar rounded-10 bg-block-bg shadow-md dark:shadow-block-inverted cursor-default print:hidden",
          className,
        )}
        aria-label={t("label")}
        {...rest}
        onMouseDown={(e) => {
          rest.onMouseDown?.(e);
          e.stopPropagation();

          if (!suggestions.query) {
            e.preventDefault();
          }
        }}
        onClick={(e) => {
          rest.onClick?.(e);
          e.stopPropagation();
        }}
      >
        {searchable && <SuggestionsSearchInput suggestions={suggestions} />}
        <div className="flex flex-col p-1.5 min-h-0">
          {searchable && suggestions.query && groups.length === 0 && (
            <div className="px-3 py-2 text-secondary text-center">{t("search.empty")}</div>
          )}
          {groups.map((group, groupIndex) => (
            <SuggestionGroupTitle
              key={group.name}
              group={group}
              groupIndex={groupIndex}
              hideTitle={groups.length === 1 && group.items.length === 1}
            >
              {group.view === "grid" ? (
                <SuggestionsGrid
                  doc={doc}
                  suggestions={suggestions}
                  group={group}
                  showHotKeys={!isNested}
                  listId={listId}
                />
              ) : (
                <SuggestionsList
                  doc={doc}
                  suggestions={suggestions}
                  group={group}
                  pseudoFocusIndex={pseudoFocusIndex}
                  showHotKeys={!isNested}
                  listId={listId}
                />
              )}
            </SuggestionGroupTitle>
          ))}
          {searchable && !suggestions.query && (
            <>
              <hr className="mx-1.5 my-1 border-gray-200 dark:border-gray-600" />
              <div className="flex flex-col gap-1 px-2 py-1 text-size-14 text-gray-500">
                <div className="whitespace-pre-line">{t("search.systemMenuHint")}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </FloatingNode>
  );
});
